import { useState } from 'react'
import { useSnackbar } from 'notistack'

import CsvExportIcon from '@mui/icons-material/FileDownloadOutlined'
import { CircularProgress } from '@mui/material'
import { useHeaderStore } from '@/stores/useHeaderStore'
import { notifyError } from '@/utils/notifyError'

import { CheckboxMenuItem } from '../CheckboxMenuItem/CheckboxMenuItem'
import { useCheckboxMenuContext } from '../context/CheckboxMenuContext'
import { CheckboxMenuItemLike } from '../index.types'

type Props<Data extends Record<string, unknown>, AugmentedData = Data> = CheckboxMenuItemLike & {
  formatCsvRow?: (row: AugmentedData) => string[]
  csvHeaders?: string[]
  csvFileName?: string
  /**
   * Optional function to augment/transform the selected rows with additional data before export.
   * Useful for loading related data that's not available in the table rows.
   */
  augmentData?: (rows: Data[]) => Promise<AugmentedData[]> | AugmentedData[]
}

export const CheckboxExportSelectedToCsvItem = <
  Data extends Record<string, unknown>,
  AugmentedData = Data
>({
  csvFileName,
  formatCsvRow,
  csvHeaders,
  augmentData
}: Props<Data, AugmentedData>) => {
  const { rows, closeMenu, deselectAll } = useCheckboxMenuContext<Data>()
  const { title } = useHeaderStore()
  const { enqueueSnackbar } = useSnackbar()
  const titleSpaceReplacedWithUnderscore = title?.replace(/\s+/g, '_') || 'export'
  const [isLoading, setIsLoading] = useState(false)

  const escapeCsvValue = (value: string): string => {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  const exportToCsv = async () => {
    try {
      setIsLoading(true)

      let dataToExport: AugmentedData[]
      if (augmentData) {
        dataToExport = await augmentData(rows)
      } else {
        dataToExport = rows as unknown as AugmentedData[]
      }

      const headers = csvHeaders ? csvHeaders.map(escapeCsvValue).join(',') + '\n' : ''

      const csvRows = dataToExport.map((row) => {
        if (formatCsvRow) {
          return formatCsvRow(row)
            .map((value) => escapeCsvValue(String(value)))
            .join(',')
        }
        return Object.values(row as Record<string, unknown>)
          .map((value) => escapeCsvValue(String(value)))
          .join(',')
      })

      const csvContent = headers + csvRows.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `${csvFileName || titleSpaceReplacedWithUnderscore}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      enqueueSnackbar('CSV exported successfully', { variant: 'success' })

      deselectAll?.()
      closeMenu?.()
    } catch (error) {
      console.error('Error exporting CSV:', error)
      notifyError('Failed to export CSV. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CheckboxMenuItem
      text="Export To CSV"
      onClick={exportToCsv}
      icon={isLoading ? <CircularProgress size={16} /> : <CsvExportIcon />}
      disabled={isLoading}
    />
  )
}
